import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Icon, Icons, Typography, TypographyType } from '../../components';
import { SCREEN_SIZE_BREAKPOINTS } from '../../constants';
import { useTimer, useWindowResize } from '../../hooks';
import { TextColor } from '../../types';

const PANEL_COLLAPSE_DELAY = 300;

interface ProspectPagePanelProps {
  children: React.ReactNode;
}

const ProspectPagePanel = ({ children }: ProspectPagePanelProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHalfOpen, setIsHalfOpen] = useState(true);
  const [triggerWidth, setTriggerWidth] = useState(0);

  const triggerRef = useRef<HTMLDivElement>(null);

  const windowWidth = useWindowResize();

  const { startTimer, stopTimer } = useTimer({
    callback: () => setIsHalfOpen(true),
    delay: PANEL_COLLAPSE_DELAY / 2,
  });

  // Automatically collapse the panel for window widths less than or equal to the threshold.
  useEffect(() => {
    setIsCollapsed((prev) => {
      if (windowWidth > SCREEN_SIZE_BREAKPOINTS.TABLET && prev) return false;
      if (windowWidth <= SCREEN_SIZE_BREAKPOINTS.TABLET && !prev) return true;
      return prev;
    });
  }, [windowWidth]);

  useEffect(() => {
    if (isCollapsed) {
      setIsHalfOpen(false);
    } else {
      startTimer();
    }

    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.clientWidth);
    }

    return () => {
      stopTimer();
    };
  }, [isCollapsed, startTimer, stopTimer]);

  return (
    <div
      className={clsx(
        'relative box-border flex flex-col rounded-l-lg bg-base-150 py-6 shadow-dialog transition-[border,width,padding]',
        isCollapsed ? 'w-0 border-none px-0' : 'w-[600px] border px-6'
      )}
      style={{
        transitionDuration: `${PANEL_COLLAPSE_DELAY}ms`,
      }}
    >
      <div
        className="absolute right-0 flex h-7 w-fit translate-y-2 cursor-pointer rounded-r-[4px]"
        ref={triggerRef}
        style={{ left: -triggerWidth }}
        onClick={() => setIsCollapsed((prev) => !prev)}
      >
        <div
          className={clsx(
            'flex h-full items-center justify-center gap-2 rounded-l-[4px] border-y border-l bg-base-150',
            isCollapsed && 'pr-[6px] shadow-dialog'
          )}
        >
          <Icons icon={isCollapsed ? Icon.CHEVRON_LEFT : Icon.CHEVRON_RIGHT} color={TextColor.SECONDARY} />
          {isCollapsed && <Icons icon={Icon.KNOWLEDGE_HUB} color={TextColor.SECONDARY} />}
        </div>
      </div>
      {isHalfOpen && (
        <div className="flex h-full flex-col gap-4">
          <Typography type={TypographyType.LABEL}>Knowledge Hub</Typography>
          {children}
        </div>
      )}
    </div>
  );
};

export default ProspectPagePanel;
